import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { messageAPI, matchAPI, bookingAPI, previewAPI } from '../services/api';
import socketService from '../services/socket';
import DisplayName from '../components/DisplayName';
import BookingModal from '../components/BookingModal';
import './Chat.css';

const Chat = () => {
  const { matchId } = useParams();
  const { user, demoMode, isBrand } = useAuth();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [bookingAmbassador, setBookingAmbassador] = useState(null);
  const [showBookingSuccess, setShowBookingSuccess] = useState(false);
  const [bookingAutoConfirmed, setBookingAutoConfirmed] = useState(false);

  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await messageAPI.getMessages(matchId);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      if (error.response?.status === 403) {
        navigate('/connections/matches');
      }
    } finally {
      setLoading(false);
    }
  }, [matchId, navigate]);

  const fetchMatchData = useCallback(async () => {
    try {
      const response = await matchAPI.getMatches();
      const match = response.data.matches.find(m => m.match_id === parseInt(matchId));
      setMatchData(match);
    } catch (error) {
      console.error('Failed to fetch match data:', error);
    }
  }, [matchId]);

  useEffect(() => {
    console.log('🔧 Setting up Chat component for match:', matchId);
    fetchMessages();
    fetchMatchData();
    socketService.joinMatch(matchId);

    // Set up socket listeners
    console.log('🎧 Setting up socket listeners...');
    socketService.onNewMessage(handleNewMessage);
    socketService.onUserTyping(handleUserTyping);
    socketService.onUserStopTyping(handleUserStopTyping);
    console.log('✓ Socket listeners configured');

    return () => {
      console.log('🔌 Cleaning up socket listeners for match:', matchId);
      socketService.leaveMatch(matchId);
      socketService.removeAllListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, fetchMessages, fetchMatchData]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleNewMessage = (message) => {
    setMessages((prev) => [...prev, message]);
  };

  const handleUserTyping = (data) => {
    console.log('🔔 Received user_typing event:', data);
    console.log('   Current user ID:', user.id);
    console.log('   Typing user ID:', data.userId);

    // Only show typing indicator if it's the other person typing, not us
    if (data.userId !== user.id) {
      console.log('✓ Showing typing indicator');
      setIsTyping(true);
    } else {
      console.log('✗ Not showing typing indicator (same user)');
    }
  };

  const handleUserStopTyping = (data) => {
    console.log('🔔 Received user_stop_typing event:', data);

    // Only hide typing indicator if it's the other person who stopped typing
    if (data.userId !== user.id) {
      console.log('✓ Hiding typing indicator');
      setIsTyping(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);

    // Send typing indicator
    socketService.typing(matchId);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      socketService.stopTyping(matchId);
    }, 1000);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim() || sending) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Stop typing indicator
    socketService.stopTyping(matchId);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    try {
      // Send via socket for real-time delivery
      socketService.sendMessage(matchId, messageContent);
    } catch (error) {
      console.error('Failed to send message:', error);
      setNewMessage(messageContent); // Restore message on error
    } finally {
      setSending(false);
    }
  };

  const handleGenerateAIMessage = async () => {
    console.log('🤖 AI button clicked');
    console.log('   generatingAI:', generatingAI);
    console.log('   matchId:', matchId);
    console.log('   demoMode:', demoMode);

    if (generatingAI) {
      console.log('   ⏸️  Already generating, skipping');
      return;
    }

    // Ensure matchId is available
    if (!matchId) {
      console.error('❌ Cannot generate AI message: matchId is not available');
      alert('Please wait a moment and try again.');
      return;
    }

    console.log('   ✅ Validation passed, calling API...');
    setGeneratingAI(true);

    try {
      // Explicitly convert demoMode to boolean to ensure it's never undefined
      const isDemoMode = demoMode === true;
      console.log('   📡 Calling previewAPI.generateBrandMessage with:', { matchId, isDemoMode });

      const response = await previewAPI.generateBrandMessage(matchId, isDemoMode);
      console.log('   ✅ API response received:', response.data);

      const aiMessage = response.data.message;

      // Populate the input field with AI-generated message
      setNewMessage(aiMessage);
      console.log('   ✅ Message set successfully');
    } catch (error) {
      console.error('❌ Failed to generate AI message:', error);
      console.error('   Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      const errorMsg = error.response?.data?.error || 'Failed to generate AI message. Please try again.';
      alert(errorMsg);
    } finally {
      setGeneratingAI(false);
      console.log('   🏁 Generation complete');
    }
  };

  // Format time from 24-hour to 12-hour format with AM/PM
  const formatTime24to12 = (time24) => {
    if (!time24) return '';

    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;

    return `${hour12}:${minutes} ${ampm}`;
  };

  // Parse date string as local date (not UTC) to prevent timezone shifting
  const parseLocalDate = (dateString) => {
    if (!dateString) {
      console.error('parseLocalDate: No date string provided');
      return new Date();
    }

    const dateOnly = dateString.split('T')[0];
    const [year, month, day] = dateOnly.split('-').map(Number);

    if (!year || !month || !day || month < 1 || month > 12 || day < 1 || day > 31) {
      console.error('parseLocalDate: Invalid date string:', dateString);
      return new Date();
    }

    return new Date(year, month - 1, day);
  };

  const handleBookingSubmit = async (bookingData) => {
    try {
      if (!matchData) {
        alert('Error: Could not find match. Please try again.');
        return;
      }

      // Create booking in database
      const bookingPayload = {
        matchId: parseInt(matchId),
        ambassadorId: bookingData.ambassadorId,
        eventName: bookingData.eventName,
        eventDate: bookingData.eventDate,
        startTime: bookingData.startTime,
        endTime: bookingData.endTime,
        duration: bookingData.duration,
        eventLocation: bookingData.eventLocation,
        hourlyRate: bookingData.hourlyRate,
        totalCost: bookingData.estimatedCost,
        notes: bookingData.notes,
      };

      const response = await bookingAPI.createBooking(bookingPayload);
      const isAutoConfirmed = response.data.autoConfirmed || false;

      // Send a message in the chat with booking details
      const bookingMessage = `📅 ${isAutoConfirmed ? 'Booking Confirmed!' : 'New Booking Request'}

Event: ${bookingData.eventName}
Date: ${parseLocalDate(bookingData.eventDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
Time: ${formatTime24to12(bookingData.startTime)} - ${formatTime24to12(bookingData.endTime)} CST (${bookingData.duration} hours)
Location: ${bookingData.eventLocation}${bookingData.notes ? `\nNotes: ${bookingData.notes}` : ''}

Rate: $${bookingData.hourlyRate}/hour
Total Cost: $${bookingData.estimatedCost.toFixed(2)}

Status: ${isAutoConfirmed ? '✅ Confirmed' : 'Pending confirmation'}`;

      // Send via socket for real-time delivery
      socketService.sendMessage(parseInt(matchId), bookingMessage);

      // Close modal and show success
      setBookingAmbassador(null);
      setBookingAutoConfirmed(isAutoConfirmed);
      setShowBookingSuccess(true);
    } catch (error) {
      console.error('Failed to create booking:', error);

      const errorMessage = error.response?.data?.error || 'Failed to create booking. Please try again.';
      alert(errorMessage);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      });
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="chat-container">
        <div className="loading">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-header">
        <button className="back-button" onClick={() => navigate('/connections/matches')}>
          ← Back
        </button>
        <h2>Chat</h2>
      </div>

      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="empty-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((message, index) => {
              // Check if message is from current user OR if AM sent it while acting as brand
              const isMine = message.sender_id === user.id ||
                            (message.created_by_am_id && message.created_by_am_id === user.id);
              const isFirstMessage = index === 0;

              return (
                <div
                  key={message.id}
                  className={`message ${isMine ? 'mine' : 'theirs'}`}
                >
                  {!isMine && (message.am_profile_photo || message.sender_photo) && (
                    <img
                      src={message.am_profile_photo || message.sender_photo}
                      alt={message.am_name || message.sender_name}
                      className="message-avatar"
                    />
                  )}
                  <div className="message-content">
                    {!isMine && message.sender_name && (
                      <p className="message-sender">
                        {message.am_name ? (
                          // Message sent by AM acting as brand - show "AM Name: Company"
                          `${message.am_name}: ${message.company_name || message.sender_name}`
                        ) : (
                          // Regular message - use DisplayName component
                          <DisplayName
                            user={{ id: message.sender_id, name: message.sender_name, is_test: message.is_test }}
                            demoMode={demoMode}
                          />
                        )}
                      </p>
                    )}
                    <div className="message-bubble">
                      <p>{message.content}</p>
                    </div>
                    <p className="message-time">{formatTime(message.created_at)}</p>
                    {isFirstMessage && (
                      <p className="auto-message-label">Sent automatically when matched</p>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}

        {isTyping && (
          <div className="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        )}

        {/* Debug indicator */}
        {isTyping && console.log('💬 Rendering typing indicator - isTyping:', isTyping)}
      </div>

      {/* Action Bar - Only show for brands */}
      {isBrand && matchData && (
        <div className="chat-action-bar">
          <button
            className="action-bar-button book-button"
            onClick={() => {
              setBookingAmbassador({
                id: matchData.user_id,
                name: matchData.name,
                hourly_rate: matchData.hourly_rate,
                profile_photo: matchData.profile_photo,
                is_test: matchData.is_test,
              });
            }}
          >
            <span className="action-icon">📅</span>
            Book Now
          </button>
        </div>
      )}

      <form onSubmit={handleSendMessage} className="message-form">
        <input
          type="text"
          value={newMessage}
          onChange={handleInputChange}
          placeholder="Type a message..."
          className="message-input"
          disabled={sending}
        />
        {user?.isPreview && isBrand && (
          <button
            type="button"
            className="ai-generate-button"
            onClick={handleGenerateAIMessage}
            disabled={generatingAI || sending}
          >
            {generatingAI ? '✨ Generating...' : '✨ AI'}
          </button>
        )}
        <button
          type="submit"
          className="send-button"
          disabled={!newMessage.trim() || sending}
        >
          Send
        </button>
      </form>

      {/* Booking Modal */}
      {bookingAmbassador && (
        <BookingModal
          ambassador={bookingAmbassador}
          onClose={() => setBookingAmbassador(null)}
          onSubmit={handleBookingSubmit}
        />
      )}

      {/* Booking Success Modal */}
      {showBookingSuccess && (
        <div className="booking-success-modal" onClick={() => setShowBookingSuccess(false)}>
          <div className="booking-success-content" onClick={(e) => e.stopPropagation()}>
            <div className="booking-success-icon">✅</div>
            <h2>{bookingAutoConfirmed ? 'Booking Confirmed!' : 'Booking Request Sent!'}</h2>
            <p>
              {bookingAutoConfirmed
                ? 'Your booking is confirmed! View in Calendar to manage the activation.'
                : 'Your booking request has been sent and is pending confirmation from the ambassador.'}
            </p>
            <div className="booking-success-actions">
              <button
                className="view-calendar-button"
                onClick={() => {
                  setShowBookingSuccess(false);
                  navigate('/calendar');
                }}
              >
                View in Calendar
              </button>
              <button
                className="dismiss-button"
                onClick={() => setShowBookingSuccess(false)}
              >
                Stay in Chat
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Chat;
